import urlProcessor from '@app/helpers/urlProcessor'
import { wordPressRequest } from "@/modules/wordpress";
import { ref, computed, isRef } from 'vue'
import { toFormData } from '@app/helpers/formData';
import { deepClone } from '@app/helpers/json';

const guessContentType = (requestBody) => {
    //If this is left as null it'll fill it with the correct boundaries
    if (requestBody instanceof FormData) {
        return null
    }

    //If we're sending an object which isn't form data we should presume its json
    if (requestBody instanceof Object) {
        return 'application/json'
    }

    //Unsure what the content type would be
    return null
}

/**
 * Create an object which contains the ability to send a wp ajax request
 * 
 * @param {String}      initialAction             The action the wordpress ajax request is listening for
 * @param {String}      initialMethod             The HTTP method for the request
 * @param {Object}      config                    The configuration for the api
 * @param {String|null} config.contentType        The content type for the body, if null it's guessed as the request is executed
 * @param {Object}      config.parameters         The default request body
 * @param {Function}    config.onDataRetrieved    The callback to run through the data after the response
 * @param {Function}    config.preRequestCallback The callback to run the data through before the request is made
 * 
 * @returns {Object}           api
 * @returns {Ref<String>}      api.url            The url for the request
 * @returns {Ref<String>}      api.nonce          The nonce for the request
 * @returns {Ref<Object>}      api.parameters     The body parameters for the request
 * @returns {Ref<String>}      api.method         The method to send the request for
 * @returns {Ref<String>}      api.action         The action to send the request for
 * @returns {Ref<String|null>} api.contentType    The content type for the request body, guessed if its left null
 * @returns {Ref<Function>}    api.callback       The function to run when we've retrieved the response from the API
 * @returns {Ref<Function>}    api.beforeRequest  The function to run against the body to format the data before the request
 */
export function createApi(initialAction, initialMethod = 'GET', config = {}) {
    /**
     * The request method
     */
    const method = ref(initialMethod)

    /**
     * The default configuration to fallback to if parameters aren't defined
     */
    const defaultConfig = {
        contentType: null,
        parameters: {},
        onDataRetrieved: data => data,
        /**
         * For GET requests this callback is applied to the data
         *  the response is then passed into the urlProcessor which parses the parameters into a query string
         *  so the function must return an object
         *      Mainly should be used for appending data at runtime
         * 
         * For other requests this is applied to the data and passed into the body of the request
         *  so to allow image uploads natively we should convert the object to FormData
         * 
         * @param {Object} data 
         * @returns {*}
         */
        preRequestCallback: (data) => method.value === 'GET'
            ? data
            : toFormData(data),
    }

    config = {
        ...defaultConfig,
        ...config
    }

    /**
     * The default entrypoint for API requests
     */
    const url = ref(window.phpVals.adminAjax)

    /**
     * The security nonce to send with the request
     */
    const nonce = ref(window.phpVals.btNonce)

    /**
     * The parameters used to send the request with (body)
     */
    const parameters = ref(deepClone(config.parameters))

    /**
     * The action to send the request for (bound as a parameter with the request body)
     */
    const action = isRef(initialAction)
        ? initialAction
        : ref(initialAction)

    /**
     * The method to call on execution of the request
     */
    const callback = ref(config.onDataRetrieved)

    /**
     * The content type of the request body
     *  - if left null we will attempt to match it automatically
     */
    const contentType = ref(config.contentType)

    /**
     * The callback to apply to the request body as its generated
     */
    const preRequestCallback = ref(config.preRequestCallback)

    const sentData = ref({})

    /**
     * The parameters to send as the body of the request
     *      - The body isn't sent on get requests as its parsed into the url
     */
    const requestParameters = computed(
        () => ({
            ...parameters.value,
            action: action.value,
            nonce: nonce.value
        })
    )

    /**
     * The url to send the request to
     *      - The body is sent as query parameters on GET requests
     */
    const requestUrl = computed(
        () => method.value === 'GET'
            ? urlProcessor(
                url.value,
                preRequestCallback.value(requestParameters.value ?? {})
            )
            : url.value
    )


    return {
        url: url,
        nonce: nonce,

        parameters: parameters,
        method: method,
        action: action,

        contentType: contentType,
        callback: callback,

        beforeRequest: preRequestCallback,

        sentData: sentData,

        /**
         * Asyncronous callback to run the API call
         * 
         * @returns {*}
         */
        execute: async () => {
            //Using structureClone doesn't work as some objects are proxies
            sentData.value = deepClone(requestParameters.value)

            const requestBody = preRequestCallback.value(requestParameters.value)

            const guessedContentType = contentType.value !== null
                ? contentType.value
                : guessContentType(requestBody)

            return await callback.value(
                await wordPressRequest(
                    method.value,
                    requestUrl.value,
                    method.value === 'GET'
                        ? null
                        : requestBody,
                    guessedContentType
                )
            )
        },

        reset: () => {
            parameters.value = deepClone(config.parameters)
        },
    }
















}